import type { Item } from "@kaidoki/proto";
import { CalendarDays, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { formatDate } from "../lib/format";
import {
  getDaysUntilDue,
  getItemStatus,
  getProgressPercentage,
  statusConfig,
} from "../lib/status";
import { ItemThumbnail } from "./ItemThumbnail";

type Props = {
  item: Item;
  onSelect?: (itemId: string) => void;
  onPurchase?: (item: Item) => void;
  isPurchasing?: boolean;
};

export function PurchaseCheckIndicator({
  isPurchasing = false,
}: {
  isPurchasing?: boolean;
}) {
  return isPurchasing ? (
    <span className="h-9 w-9 animate-spin rounded-full border-[2.5px] border-blue-100 border-t-blue-500" />
  ) : (
    <span className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-blue-500">
      <Check size={25} strokeWidth={2.5} />
    </span>
  );
}

const badgeClasses = {
  overdue: "bg-red-50 text-red-600",
  due_soon: "bg-amber-50 text-amber-600",
  upcoming: "bg-yellow-50 text-yellow-600",
  ok: "bg-green-50 text-green-600",
} as const;

export function ItemCard({
  item,
  onSelect,
  onPurchase,
  isPurchasing = false,
}: Props) {
  const { t } = useTranslation(["items", "common"]);
  const navigate = useNavigate();
  const status = getItemStatus(item.nextDueAt);
  const config = statusConfig[status];
  const daysUntil = getDaysUntilDue(item.nextDueAt);
  const progress = getProgressPercentage(item.nextDueAt, item.intervalDays);
  const dueLabel =
    daysUntil < 0
      ? t("items:card.daysOverdue", { count: Math.abs(daysUntil) })
      : daysUntil === 0
        ? t("items:card.dueToday")
        : t("items:card.daysLeft", { count: daysUntil });

  return (
    <article className="flex min-h-[104px] overflow-hidden rounded-[14px] border border-[#e3e8f0] bg-white transition-[border-color,box-shadow] hover:border-blue-200 hover:shadow-sm">
      <button
        type="button"
        onClick={() =>
          onSelect ? onSelect(item.id) : navigate(`/items/${item.id}`)
        }
        className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3 text-left sm:px-4"
        aria-label={`${item.name}, ${dueLabel}`}
      >
        <ItemThumbnail iconUrl={item.iconUrl} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-bold text-gray-900">
            {item.name}
          </h3>
          <div className="mt-2 flex min-w-0 flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
            <span className="flex min-w-0 items-center gap-2">
              <CalendarDays size={14} className="shrink-0 text-gray-400" />
              <span className="whitespace-nowrap text-xs tabular-nums text-gray-500 sm:min-w-0 sm:flex-1 sm:truncate">
                {t("items:card.nextDue", {
                  date: formatDate(item.nextDueAt),
                  defaultValue: `次回: ${formatDate(item.nextDueAt)}`,
                })}
              </span>
            </span>
            <span
              className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold tabular-nums ${badgeClasses[status]}`}
            >
              {dueLabel}
            </span>
          </div>
          <div className="mt-3 h-[3px] w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className={`h-full rounded-full transition-[width] duration-300 ${config.barColor}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </button>

      {onPurchase && (
        <button
          type="button"
          onClick={() => onPurchase(item)}
          disabled={isPurchasing}
          className="flex w-[68px] shrink-0 items-center justify-center bg-white text-blue-500 transition-transform active:scale-95 disabled:cursor-wait"
          aria-label={`${item.name} ${t("items:purchase.button")}`}
        >
          <PurchaseCheckIndicator isPurchasing={isPurchasing} />
        </button>
      )}
    </article>
  );
}
